import { z } from 'zod';
import type { WebsiteUpsertRequest } from 'src/api/generated';

const emptyStringToUndefined = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length === 0 ? undefined : trimmedValue;
};

const optionalEmailSchema = z.preprocess(
  emptyStringToUndefined,
  z.email().optional()
);

const optionalHttpUrlSchema = z.preprocess(
  emptyStringToUndefined,
  z.url({ protocol: /^https?$/ }).optional()
);

/**
 * Checks an optional website contact email using the shared contract rules.
 *
 * @param value - Contact email entered in website settings.
 * @returns Whether the value is blank or a valid email address.
 */
export const isWebsiteContactEmailValid = (value?: string): boolean =>
  optionalEmailSchema.safeParse(value).success;

/**
 * Checks an optional website social link using the shared contract rules.
 *
 * @param value - Social URL entered in website settings.
 * @returns Whether the value is blank or an absolute HTTP(S) URL.
 */
export const isWebsiteContactHttpUrlValid = (value?: string): boolean =>
  optionalHttpUrlSchema.safeParse(value).success;

/**
 * Validates every formatted contact field before a website operation.
 *
 * @param content - Website content from the generated API request contract.
 * @returns Whether all optional formatted contact fields are valid.
 */
export const isWebsiteContactInformationValid = (
  content: WebsiteUpsertRequest['draftConfig']['content']
): boolean =>
  isWebsiteContactEmailValid(content.email) &&
  isWebsiteContactHttpUrlValid(content.instagramUrl) &&
  isWebsiteContactHttpUrlValid(content.facebookUrl) &&
  isWebsiteContactHttpUrlValid(content.tiktokUrl);
